import React, { useState, useEffect, useCallback } from 'react';
import { useWeddingContext } from '../context/WeddingContext';

interface DeleteConfirmModalProps {
  taskId: string;
  onClose: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ taskId, onClose }) => {
  const { tasks, deleteTask } = useWeddingContext();
  const [isDeleting, setIsDeleting] = useState(false);
  
  const task = tasks.find(t => (t.taskId || t.id) === taskId);

  // onCloseを安定化
  const stableOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // タスクが見つからない場合の処理をuseEffectで行う
  useEffect(() => {
    if (!task) {
      stableOnClose();
    }
  }, [task, stableOnClose]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTask(taskId);
      stableOnClose();
    } catch (error) {
      console.error('Failed to delete task:', error);
      // エラーが発生した場合もモーダルを閉じる
      stableOnClose();
    } finally {
      setIsDeleting(false);
    }
  };

  // タスクが見つからない場合は何も表示しない
  if (!task) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <span className="text-red-600 text-xl">⚠️</span>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            タスクを削除しますか？
          </h3>
          
          <div className="mb-6">
            <p className="text-sm text-gray-500 mb-2">
              以下のタスクを削除します。この操作は取り消せません。
            </p>
            <div className="bg-gray-50 p-3 rounded-lg border">
              <p className="font-medium text-gray-900">{task.name}</p>
              {task.description && (
                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
              )}
              <div className="flex items-center space-x-2 mt-2 text-xs">
                <span className="bg-pink-100 text-pink-800 px-2 py-1 rounded">
                  {task.category}
                </span>
                {task.assignee && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {task.assignee}
                  </span>
                )}
                {task.dueDate && (
                  <span className="text-gray-600">
                    期限: {new Date(task.dueDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={stableOnClose}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-150"
              disabled={isDeleting}
            >
              キャンセル
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className={`flex-1 font-semibold py-2 px-4 rounded-lg transition duration-150 ${
                isDeleting 
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
            >
              {isDeleting ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  削除中...
                </>
              ) : (
                '削除'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal; 